import { enqueueSnackbar } from 'notistack';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { DicomStructureSet } from '~/domain/dicom/dicomstructureset';
import { Patient } from '~/domain/patient/patient';
import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import { OperationPayload, StepPayload, StructureReference } from './types';
import { validateResultNameFormat } from './validationUtils';
import {
    buildFormulaFromSteps,
    buildPayloadFromText,
} from './booleanExpressionSyntax';

export async function pollUntil<T>(
    checkFn: () => T | null,
    maxWaitMs: number,
    pollIntervalMs = 50
): Promise<T | null> {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
        const result = checkFn();
        if (result) {
            return result;
        }
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
    return null;
}

/**
 * Finds a structure reference by name, prioritizing the current structure set.
 */
export function findStructureReference(
    structureName: string,
    patient: Patient,
    currentStructureSet: StructureSetReadModel | null
): StructureReference | null {
    const normalizedName = structureName.trim();

    // Search all structure sets, but prioritize current one if provided
    const structureSetsToSearch: DicomStructureSet[] = [];
    let currentStructureSetDicom: DicomStructureSet | null = null;

    for (const series of patient.dataSet.series) {
        if (
            series.seriesModule.modality !== 'RTSTRUCT' ||
            !series.dicomStructureSets
        ) {
            continue;
        }

        if (currentStructureSet) {
            const matching = series.dicomStructureSets.find(
                (ss: DicomStructureSet) =>
                    ss.sopInstanceUid === currentStructureSet.dicomUid
            );
            if (matching) {
                currentStructureSetDicom = matching;
            }
        }

        structureSetsToSearch.push(...series.dicomStructureSets);
    }

    // First check current structure set if available
    if (currentStructureSetDicom) {
        const roi = currentStructureSetDicom.findRoiByName(normalizedName);
        if (roi) {
            return { roi, structureSet: currentStructureSetDicom };
        }
    }

    // Fallback: search all structure sets
    for (const structureSet of structureSetsToSearch) {
        // Skip if we already checked it above
        if (structureSet === currentStructureSetDicom) {
            continue;
        }
        const roi = structureSet.findRoiByName(normalizedName);
        if (roi) {
            return { roi, structureSet };
        }
    }

    return null;
}

/**
 * Finds the DICOM structure set matching the current structure set read model.
 */
function findMatchingDicomStructureSet(
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): DicomStructureSet | null {
    for (const series of patient.dataSet.series) {
        if (
            series.seriesModule.modality !== 'RTSTRUCT' ||
            !series.dicomStructureSets
        ) {
            continue;
        }

        const matching = series.dicomStructureSets.find(
            (ss: DicomStructureSet) =>
                ss.sopInstanceUid === currentStructureSet.dicomUid
        );

        if (matching) {
            return matching;
        }
    }
    return null;
}

/**
 * Converts a ROI read model to a structure reference by ROI number, falling back to name lookup.
 */
export function roiReadModelToStructureReference(
    roiModel: { roiNumber: number; name: string },
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): StructureReference | null {
    const matchingStructureSet = findMatchingDicomStructureSet(
        patient,
        currentStructureSet
    );

    if (!matchingStructureSet) {
        return null;
    }

    // Try to find by ROI number in the matching structure set
    const roi = matchingStructureSet.rois.get(roiModel.roiNumber);
    if (roi) {
        return { roi, structureSet: matchingStructureSet };
    }

    // Fallback: search all structure sets by ROI number
    for (const series of patient.dataSet.series) {
        if (
            series.seriesModule.modality !== 'RTSTRUCT' ||
            !series.dicomStructureSets
        ) {
            continue;
        }

        for (const structureSet of series.dicomStructureSets) {
            const roi = structureSet.rois.get(roiModel.roiNumber);
            if (roi) {
                return { roi, structureSet };
            }
        }
    }

    // Final fallback: search by name
    return findStructureReference(roiModel.name, patient, currentStructureSet);
}

/**
 * Converts right operand ROI models to structure references.
 */
function convertRightOperandsToReferences(
    rightOperands: RoiReadModel[],
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): StructureReference[] {
    return rightOperands
        .map((s) =>
            roiReadModelToStructureReference(s, patient, currentStructureSet)
        )
        .filter((ref): ref is StructureReference => ref !== null);
}

/**
 * Converts a UI step to a StepPayload for formula building.
 */
function convertStepToPayload(
    step: {
        leftOperand: RoiReadModel | null;
        rightOperands: RoiReadModel[];
        operation: string;
    },
    stepIndex: number,
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): StepPayload | null {
    const rightRefs = convertRightOperandsToReferences(
        step.rightOperands,
        patient,
        currentStructureSet
    );

    if (rightRefs.length === 0) {
        return null;
    }

    if (stepIndex === 0) {
        if (!step.leftOperand) {
            return null;
        }
        const leftRef = roiReadModelToStructureReference(
            step.leftOperand,
            patient,
            currentStructureSet
        );
        if (!leftRef) {
            return null;
        }

        return {
            leftOperand: leftRef,
            rightOperands: rightRefs,
            operation: step.operation as BooleanOperation,
        };
    }

    return {
        leftOperand: 'previous-result',
        rightOperands: rightRefs,
        operation: step.operation as BooleanOperation,
    };
}

/**
 * Builds formula text from UI steps.
 */
export function buildFormulaText(
    steps: Array<{
        leftOperand: RoiReadModel | null;
        rightOperands: RoiReadModel[];
        operation: string;
    }>,
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): string {
    if (!steps.length) {
        return '';
    }

    try {
        const payloadSteps: StepPayload[] = [];
        for (let i = 0; i < steps.length; i++) {
            const stepPayload = convertStepToPayload(
                steps[i]!,
                i,
                patient,
                currentStructureSet
            );
            if (stepPayload) {
                payloadSteps.push(stepPayload);
            } else if (i === 0) {
                // First step failed - can't build formula
                return '';
            }
            // Subsequent steps can be skipped if they fail
        }

        if (payloadSteps.length === 0) {
            return '';
        }

        return buildFormulaFromSteps(payloadSteps);
    } catch (error) {
        console.error('Error building formula text:', error);
        return '';
    }
}

/**
 * Validates and converts a step to StepPayload with error handling.
 */
function convertStepToPayloadWithValidation(
    step: {
        leftOperand: RoiReadModel | null;
        rightOperands: RoiReadModel[];
        operation: string;
    },
    stepIndex: number,
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): StepPayload | null {
    const rightRefs = convertRightOperandsToReferences(
        step.rightOperands,
        patient,
        currentStructureSet
    );

    if (rightRefs.length === 0) {
        const missingNames =
            step.rightOperands.length > 0
                ? step.rightOperands.map((s) => s.name).join(', ')
                : 'unknown';
        enqueueSnackbar(
            `Boolean operation failed: could not resolve right operand(s) for step ${
                stepIndex + 1
            } (${missingNames})`,
            { variant: 'error' }
        );
        return null;
    }

    if (stepIndex === 0) {
        if (!step.leftOperand) {
            enqueueSnackbar('First structure is required', {
                variant: 'warning',
            });
            return null;
        }

        const leftRef = roiReadModelToStructureReference(
            step.leftOperand,
            patient,
            currentStructureSet
        );
        if (!leftRef) {
            enqueueSnackbar(
                `Structure "${step.leftOperand.name}" (ROI ${step.leftOperand.roiNumber}) not found`,
                { variant: 'error' }
            );
            return null;
        }

        return {
            leftOperand: leftRef,
            rightOperands: rightRefs,
            operation: step.operation as BooleanOperation,
        };
    }

    return {
        leftOperand: 'previous-result',
        rightOperands: rightRefs,
        operation: step.operation as BooleanOperation,
    };
}

/**
 * Builds an operation payload from UI steps with validation.
 */
export function buildPayload(
    steps: Array<{
        leftOperand: RoiReadModel | null;
        rightOperands: RoiReadModel[];
        operation: string;
    }>,
    resultName: string,
    isValid: boolean,
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): OperationPayload | null {
    const nameValidation = validateResultNameFormat(resultName);
    if (!nameValidation.valid) {
        enqueueSnackbar(nameValidation.error, { variant: 'warning' });
        return null;
    }

    if (!isValid) {
        enqueueSnackbar('Please fill in all required fields', {
            variant: 'warning',
        });
        return null;
    }

    const payloadSteps: StepPayload[] = [];
    for (let i = 0; i < steps.length; i++) {
        const stepPayload = convertStepToPayloadWithValidation(
            steps[i]!,
            i,
            patient,
            currentStructureSet
        );
        if (!stepPayload) {
            return null;
        }
        payloadSteps.push(stepPayload);
    }

    return {
        steps: payloadSteps,
        resultName: resultName.trim(),
        formulaText: buildFormulaFromSteps(payloadSteps),
    };
}

export function buildPayloadFromFormula(
    formulaText: string,
    resultName: string,
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): OperationPayload | null {
    try {
        return buildPayloadFromText(
            formulaText,
            (name) =>
                findStructureReference(name, patient, currentStructureSet),
            resultName.trim() || 'Result'
        );
    } catch (error) {
        enqueueSnackbar(
            error instanceof Error
                ? error.message
                : 'Invalid boolean expression',
            { variant: 'error' }
        );
        return null;
    }
}
